import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FilterIcon, ChevronDownIcon, AwardIcon, Download } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useReportOrgScope } from './useReportOrgScope';
import ReportOrgFilterFields from './ReportOrgFilterFields';
import { REPORT_TABLE_VALIGN, ReportTh, REPORT_THEAD_ROW } from './ReportPageUi';
import { createReportCsvBlob } from '../../../utils/reportCsvExport';
import ReportLeaderboardCharts from './ReportLeaderboardCharts';
import { buildLeaderboardChartCsvAppend } from './leaderboardReportChartsUtils';

const LEADERBOARD_EXPORT_TIMEOUT_MS = 180000;

function csvEscapeCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildLeaderboardQuery(appliedExercise, orgFilters) {
  const params = new URLSearchParams();
  if (appliedExercise.dateFrom) params.set('dateFrom', appliedExercise.dateFrom);
  if (appliedExercise.dateTo) params.set('dateTo', appliedExercise.dateTo);
  if (orgFilters.departmentId) params.set('departmentId', orgFilters.departmentId);
  if (orgFilters.institutionId) params.set('institutionId', orgFilters.institutionId);
  if (orgFilters.regionId) params.set('regionId', orgFilters.regionId);
  if (orgFilters.academyId) params.set('academyId', orgFilters.academyId);
  if (orgFilters.districtId) params.set('districtId', orgFilters.districtId);
  if (orgFilters.countryId) params.set('countryId', orgFilters.countryId);
  return params.toString();
}

const ReportDailyExercises = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const org = useReportOrgScope(currentLanguage);

  const [exerciseFilters, setExerciseFilters] = useState({
    exerciseId: '',
    dateFrom: '',
    dateTo: ''
  });
  const [appliedExercise, setAppliedExercise] = useState({
    exerciseId: '',
    dateFrom: '',
    dateTo: ''
  });

  const [exercises, setExercises] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [meta, setMeta] = useState({ exerciseName: '', totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const fetchExercises = async () => {
    try {
      const response = await api.get('/exercises?page=1&limit=100');
      if (response.data.success) {
        setExercises(response.data.data.exercises || []);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error(t('reports.fetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  useEffect(() => {
    fetchExercises();
  }, [currentLanguage]);

  const fetchLeaderboard = useCallback(async () => {
    const exerciseId = appliedExercise.exerciseId;
    if (!exerciseId) {
      setLeaderboard([]);
      setMeta({ exerciseName: '', totalCount: 0 });
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedExercise.dateFrom) params.set('dateFrom', appliedExercise.dateFrom);
      if (appliedExercise.dateTo) params.set('dateTo', appliedExercise.dateTo);
      if (org.appliedFilters.departmentId) params.set('departmentId', org.appliedFilters.departmentId);
      if (org.appliedFilters.institutionId) params.set('institutionId', org.appliedFilters.institutionId);
      if (org.appliedFilters.regionId) params.set('regionId', org.appliedFilters.regionId);
      if (org.appliedFilters.academyId) params.set('academyId', org.appliedFilters.academyId);
      if (org.appliedFilters.districtId) params.set('districtId', org.appliedFilters.districtId);
      if (org.appliedFilters.countryId) params.set('countryId', org.appliedFilters.countryId);

      const url = `/exercises/${exerciseId}/leaderboard${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      if (response.data.success) {
        const data = response.data.data || {};
        setLeaderboard(data.leaderboard || []);
        setMeta({
          exerciseName: data.exerciseName || '',
          totalCount: data.totalCount != null ? data.totalCount : (data.leaderboard || []).length
        });
      } else {
        setLeaderboard([]);
        setMeta({ exerciseName: '', totalCount: 0 });
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error(t('reports.fetchError'), { duration: 3000, position: 'top-center' });
      setLeaderboard([]);
      setMeta({ exerciseName: '', totalCount: 0 });
    } finally {
      setLoading(false);
    }
  }, [appliedExercise, org.appliedFilters, t]);

  const applyFiltersHandler = () => {
    org.applyOrgFilters();
    setAppliedExercise({ ...exerciseFilters });
    org.setShowFilters(false);
  };

  useEffect(() => {
    const eid = appliedExercise.exerciseId;
    if (eid) fetchLeaderboard();
    else {
      setLeaderboard([]);
      setMeta({ exerciseName: '', totalCount: 0 });
    }
  }, [appliedExercise.exerciseId, fetchLeaderboard]);

  const clearFilters = () => {
    setExerciseFilters((prev) => ({
      ...prev,
      dateFrom: '',
      dateTo: ''
    }));
    setAppliedExercise((prev) => ({
      ...prev,
      dateFrom: '',
      dateTo: ''
    }));
    org.setShowFilters(true);
  };

  const formatAvgTime = (ms) => {
    if (ms == null || ms === '') return '-';
    const sec = Math.round(Number(ms) / 1000);
    if (sec < 60) return `${sec} ${t('reports.seconds')}`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')} ${t('reports.minutes')}`;
  };

  const getMedalStyle = (rank) => {
    if (rank === 1) return { icon: 'text-amber-500', row: 'bg-amber-50/70 border-l-4 border-amber-400' };
    if (rank === 2) return { icon: 'text-slate-400', row: 'bg-slate-50/80 border-l-4 border-slate-400' };
    if (rank === 3) return { icon: 'text-amber-700', row: 'bg-amber-100/50 border-l-4 border-amber-600' };
    return { icon: '', row: '' };
  };

  const downloadCsv = async () => {
    if (!appliedExercise.exerciseId || exportingCsv) return;
    setExportingCsv(true);
    try {
      const q = buildLeaderboardQuery(appliedExercise, org.appliedFilters);
      const url = `/exercises/${appliedExercise.exerciseId}/leaderboard${q ? `?${q}` : ''}`;
      const response = await api.get(url, { timeout: LEADERBOARD_EXPORT_TIMEOUT_MS });
      if (!response.data?.success) {
        const msg = response.data?.message || response.data?.error;
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg || 'export failed'));
      }
      const data = response.data.data || {};
      const rows = data.leaderboard || [];
      if (rows.length === 0) {
        toast.error(t('reports.users.csvNoRows'), { position: 'top-center' });
        return;
      }
      const exerciseTitle = data.exerciseName || meta.exerciseName || '';
      const headers = [
        t('reports.rank'),
        t('reports.users.colFullName'),
        t('reports.users.colStudentNo'),
        t('reports.daysPracticed'),
        t('reports.totalScore'),
        t('reports.avgTime')
      ];
      const lines = [
        [t('reports.exercise'), exerciseTitle].map(csvEscapeCell).join(','),
        headers.map(csvEscapeCell).join(',')
      ];
      for (const row of rows) {
        const name = row.fullName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '';
        const line = [
          row.rank ?? '',
          name,
          row.studentNumber ?? '',
          row.daysPracticed ?? '',
          row.totalScore != null ? Number(row.totalScore).toFixed(2) : '',
          formatAvgTime(row.avgTimeMs)
        ].map(csvEscapeCell).join(',');
        lines.push(line);
      }
      const chartCsv = buildLeaderboardChartCsvAppend(rows, t);
      const csvBody = `${lines.join('\r\n')}${chartCsv}`;
      const blob = createReportCsvBlob(csvBody);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = blobUrl;
      a.download = `daily-exercises-report-${appliedExercise.exerciseId}-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success(t('reports.users.csvSuccess', { count: rows.length }), { position: 'top-center' });
    } catch (err) {
      const res = err.response?.data;
      const serverMsg = res?.message ?? res?.error ?? res?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      const axiosMsg = err.code === 'ECONNABORTED' || err.message?.includes('timeout')
        ? t('reports.users.csvTimeout')
        : null;
      const fallback = t('reports.users.csvError');
      toast.error(axiosMsg || (detail ? String(detail) : err.message) || fallback, { duration: 6000, position: 'top-center' });
    } finally {
      setExportingCsv(false);
    }
  };

  const canExportCsv = !loading && !exportingCsv && appliedExercise.exerciseId && leaderboard.length > 0;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm shadow-slate-900/5 ring-1 ring-slate-900/[0.03]">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={() => org.setShowFilters(!org.showFilters)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <FilterIcon className="h-4 w-4" />
          {t('common.filters')}
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${org.showFilters ? 'rotate-180' : ''}`} />
        </button>
        {org.showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.exercise')} *</label>
              <select
                value={exerciseFilters.exerciseId}
                onChange={(e) => setExerciseFilters((prev) => ({ ...prev, exerciseId: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">{t('reports.selectExercise')}</option>
                {exercises.map((ex) => (
                  <option key={ex.exerciseId || ex.id} value={ex.exerciseId || ex.id}>
                    {ex.name || ex.exerciseId || ex.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.dateFrom')}</label>
              <input
                type="date"
                value={exerciseFilters.dateFrom}
                onChange={(e) => setExerciseFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.dateTo')}</label>
              <input
                type="date"
                value={exerciseFilters.dateTo}
                onChange={(e) => setExerciseFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <ReportOrgFilterFields
              filters={org.filters}
              setFilters={org.setFilters}
              lockedOrgLevels={org.lockedOrgLevels}
              countries={org.countries}
              regions={org.regions}
              academies={org.academies}
              districts={org.districts}
              institutions={org.institutions}
              departments={org.departments}
              getCountryLabel={(c) => org.getCountryTranslation(c)}
            />
          </div>
        )}
        {org.showFilters && (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={applyFiltersHandler}
              disabled={!exerciseFilters.exerciseId}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FilterIcon className="h-4 w-4" />
              {t('reports.applyFilters')}
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {t('common.clearFilters')}
            </button>
          </div>
        )}
      </div>

      <div className="p-6">
        {meta.exerciseName && (
          <p className="text-sm text-gray-600 mb-4">
            {t('reports.exercise')}: <span className="font-semibold text-gray-900">{meta.exerciseName}</span>
            {meta.totalCount != null && (
              <span className="mr-2"> • {t('reports.totalCount', { count: meta.totalCount })}</span>
            )}
          </p>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !appliedExercise.exerciseId ? (
          <p className="text-gray-500 text-center py-12">{t('reports.selectExerciseToLoad')}</p>
        ) : leaderboard.length === 0 ? (
          <p className="text-gray-500 text-center py-12">{t('reports.noData')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
              <button
                type="button"
                onClick={downloadCsv}
                disabled={!canExportCsv}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                {exportingCsv ? t('reports.users.csvExporting') : t('reports.users.downloadCsv')}
              </button>
            </div>
            <ReportLeaderboardCharts rows={leaderboard} t={t} />
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              <table className={`min-w-full ${REPORT_TABLE_VALIGN}`}>
                <thead>
                  <tr className={REPORT_THEAD_ROW}>
                    <ReportTh scope="col" className="!py-4 !px-5">
                      {t('reports.rank')}
                    </ReportTh>
                    <ReportTh scope="col" className="!py-4 !px-5">
                      {t('reports.studentNameAndNumber')}
                    </ReportTh>
                    <ReportTh scope="col" className="!py-4 !px-5">
                      {t('reports.daysPracticed')}
                    </ReportTh>
                    <ReportTh scope="col" className="!py-4 !px-5">
                      {t('reports.totalScore')}
                    </ReportTh>
                    <ReportTh scope="col" className="!py-4 !px-5">
                      {t('reports.avgTime')}
                    </ReportTh>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {leaderboard.map((row) => {
                    const medal = getMedalStyle(row.rank);
                    return (
                      <tr
                        key={row.studentUserId || row.rank}
                        className={`transition-colors hover:bg-gray-50/80 ${medal.row}`}
                      >
                        <td className="py-4 px-5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {row.rank <= 3 ? (
                              <>
                                <AwardIcon className={`h-6 w-6 flex-shrink-0 ${medal.icon}`} aria-hidden />
                                <span className="text-base font-bold text-gray-900">{row.rank}</span>
                              </>
                            ) : (
                              <span className="text-sm font-medium text-gray-700">{row.rank}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-5">
                          <div className="text-sm">
                            <span className="font-semibold text-gray-900">
                              {row.fullName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || '-'}
                            </span>
                            {row.studentNumber && (
                              <span className="text-gray-500 block text-xs mt-0.5">{row.studentNumber}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-5 whitespace-nowrap text-sm text-gray-700 font-medium tabular-nums">
                          {row.daysPracticed ?? '-'}
                        </td>
                        <td className="py-4 px-5 whitespace-nowrap text-sm font-semibold text-gray-900 tabular-nums">
                          {row.totalScore != null ? Number(row.totalScore).toFixed(2) : '-'}
                        </td>
                        <td className="py-4 px-5 whitespace-nowrap text-sm text-gray-700 tabular-nums">
                          {formatAvgTime(row.avgTimeMs)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportDailyExercises;
